import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { createContext, useCallback, useContext, useState } from "react";

const ConfirmDialogContext = createContext(() => {});

function useConfirmAction() {
  return useContext(ConfirmDialogContext);
}

function ConfirmDialogProvider({ children }) {
  // confirmation is kept during the close animation so the dialog content
  // does not flash back to the generic fallback while fading out.
  const [confirmation, setConfirmation] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const confirmAction = useCallback((description, options = {}) => {
    setConfirmation({ description, ...options });
    setIsOpen(true);
  }, []);

  const handleCancel = () => {
    const onCancel = confirmation?.onCancel;
    setIsOpen(false);
    if (!onCancel) return;
    onCancel();
  };

  const handleConfirm = () => {
    const onConfirm = confirmation?.onConfirm;
    setIsOpen(false);
    if (!onConfirm) return;
    onConfirm();
  };

  const handleSecondary = () => {
    const onSecondary = confirmation?.onSecondary;
    setIsOpen(false);
    if (!onSecondary) return;
    onSecondary();
  };

  return (
    <ConfirmDialogContext.Provider value={confirmAction}>
      {children}
      <AlertDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.title || "Confirmer cette action"}
            </AlertDialogTitle>
            {typeof confirmation?.description === "string" ? (
              <AlertDialogDescription className="text-text-secondary whitespace-pre-line">
                {confirmation.description}
              </AlertDialogDescription>
            ) : (
              <>
                <AlertDialogDescription className="sr-only">
                  Vérifiez les éléments avant de confirmer cette action.
                </AlertDialogDescription>
                {confirmation?.description ? (
                  <div className="text-text-secondary w-full text-left text-sm">
                    {confirmation.description}
                  </div>
                ) : null}
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {confirmation?.cancelLabel || "Annuler"}
            </AlertDialogCancel>
            <AlertDialogAction
              variant={
                confirmation?.destructive
                  ? "destructive"
                  : confirmation?.secondaryLabel
                    ? "outline"
                    : "default"
              }
              onClick={handleConfirm}
            >
              {confirmation?.confirmLabel || "Confirmer"}
            </AlertDialogAction>
            {confirmation?.secondaryLabel ? (
              <AlertDialogAction onClick={handleSecondary}>
                {confirmation.secondaryLabel}
              </AlertDialogAction>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmDialogContext.Provider>
  );
}

export { ConfirmDialogProvider, useConfirmAction };
